import has from "lodash/has";
import { buildUrl, getEnv } from "shared/utils";
import filterMenu from "shared/utils/filterMenu";
import { DESKTOP, MOBILE } from "shared/layout";

const { AUTH_URL, AUTH_CLIENT_ID, PROFILE_URL, SEARCH_URL } = getEnv();

const LP_URL = "https://www.lonelyplanet.com/";
const SHOP_URL = "https://shop.lonelyplanet.com/";
const IMGIX_URL = "https://lonelyplanetstatic.imgix.net/";
const VIDEO_URL = buildUrl(LP_URL, "video");
const COMMUNITY_URL = buildUrl(LP_URL, "thorntree");

const baseMenu = {
  items: [
    {
      layouts: [DESKTOP],
      title: "Search",
      url: SEARCH_URL,
    },
    {
      title: "Video",
      url: VIDEO_URL,
      mobileOrder: 3,
      submenu: {
        featured: {
          image: buildUrl(IMGIX_URL, {
            path:
              "marketing/2020/BIT/CentralAsiaSilkRoad_GettyRF_490915410.jpg",
            query: { w: 80, h: 60, fit: "min" },
          }),
          title: "Best in Travel 2020",
          subtitle: "Featured",
          url: buildUrl(VIDEO_URL, "best-in-travel-2020/v/cha/17"),
        },
        items: [
          {
            layouts: [MOBILE],
            title: "See all videos",
            url: VIDEO_URL,
          },
          {
            title: "Adventure travel",
            url: buildUrl(VIDEO_URL, "adventure-travel/v/cha/1"),
          },
          {
            title: "Art and culture",
            url: buildUrl(VIDEO_URL, "art-and-culture/v/cha/2"),
          },
          {
            title: "Beaches, coasts and islands",
            url: buildUrl(VIDEO_URL, "beaches/v/cha/3"),
          },
          {
            title: "Explore every day",
            url: buildUrl(VIDEO_URL, "explore-every-day/v/cha/4"),
          },
          {
            title: "Food and drink",
            url: buildUrl(VIDEO_URL, "food-and-drink/v/cha/7"),
          },
          {
            title: "Journeys",
            url: buildUrl(VIDEO_URL, "journeys/v/cha/9"),
          },
        ],
        call_to_action: {
          url: VIDEO_URL,
          title: "Browse Videos",
        },
      },
    },
    {
      title: "Destinations",
      url: buildUrl(LP_URL, "places"),
      mobileOrder: 1,
      submenu: {
        featured: {
          image: buildUrl(IMGIX_URL, {
            path:
              "marketing/2020/BIT/_Cairo_Egypt_shutterstockRF_1037036482.jpg",
            query: { w: 80, h: 60, fit: "min" },
          }),
          title: "Best in Travel",
          subtitle: "Featured",
          url: buildUrl(LP_URL, "best-in-travel"),
        },
        items: [
          {
            title: "Africa",
            url: buildUrl(LP_URL, "africa"),
          },
          {
            title: "Antarctica",
            url: buildUrl(LP_URL, "antarctica-1007062"),
          },
          {
            title: "Asia",
            url: buildUrl(LP_URL, "asia"),
          },
          {
            title: "Australia & Pacific",
            url: buildUrl(LP_URL, "pacific"),
          },
          {
            title: "Caribbean",
            url: buildUrl(LP_URL, "caribbean"),
          },
          {
            title: "Central America",
            url: buildUrl(LP_URL, "central-america"),
          },
          {
            title: "Europe",
            url: buildUrl(LP_URL, "europe"),
          },
          {
            title: "Middle East",
            url: buildUrl(LP_URL, "middle-east"),
          },
          {
            title: "North America",
            url: buildUrl(LP_URL, "north-america"),
          },
          {
            title: "South America",
            url: buildUrl(LP_URL, "south-america"),
          },
        ],
        call_to_action: {
          url: buildUrl(LP_URL, "places"),
          title: "See All Countries",
        },
      },
    },
    {
      title: "Bookings",
      url: null,
      mobileOrder: 2,
      submenu: {
        items: [
          {
            title: "Insurance",
            url: buildUrl(LP_URL, "travel-insurance"),
          },
          {
            title: "Hotels",
            url: buildUrl(LP_URL, "hotels"),
          },
          {
            title: "Flights",
            url: buildUrl(LP_URL, "flights"),
          },
          {
            title: "Adventure tours",
            url: buildUrl(LP_URL, "adventure-tours"),
          },
          {
            title: "Sightseeing tours",
            url: buildUrl(LP_URL, "sightseeing-tours"),
          },
          {
            title: "Car rental",
            url: buildUrl(LP_URL, "car-rental"),
          },
        ],
      },
    },
    {
      title: "Shop",
      url: SHOP_URL,
      isExternal: true,
      mobileOrder: 4,
    },
  ],
};

const getSignInItem = ({ includeTargetLink = true } = {}) => {
  const query = {
    client_id: AUTH_CLIENT_ID,
    scope: "openid",
    redirect_uri: buildUrl(AUTH_URL, "sso"),
    response_type: "code",
    prompt: "login",
  };

  if (typeof window !== "undefined") {
    if (has(window, "lp.auth.options.clientId")) {
      query.client_id = window.lp.auth.options.clientId || query.client_id;
    }

    if (includeTargetLink) {
      query.target_link_uri = window.location.href;
    }
  }

  return {
    title: "Sign in",
    url: buildUrl(AUTH_URL, { path: "oauth/authorize", query }),
  };
};

const getUserSettingsItem = (user) => ({
  title: "Settings",
  url: buildUrl(PROFILE_URL, `${user.id}/edit`),
});

const getUserProfileItem = (user) => ({
  title: "Profile",
  url: buildUrl(PROFILE_URL, `${user.username}`),
});

const getUserListsItem = (user) => ({
  title: "Lists",
  url: buildUrl(PROFILE_URL, `${user.username}/lists`),
});

const thornTreeItem = {
  title: "Thorn Tree",
  url: buildUrl(COMMUNITY_URL),
};

const messagesItem = {
  title: "Messages",
  url: buildUrl(COMMUNITY_URL, "messages"),
};

const signOutItem = {
  title: "Sign out",
  url: buildUrl(AUTH_URL, "users/sign_out"),
};

const getUserDesktopMenu = (user) => {
  const userSettingsItem = getUserSettingsItem(user);

  return {
    ...userSettingsItem,
    submenu: {
      items: [
        userSettingsItem,
        getUserProfileItem(user),
        getUserListsItem(user),
        thornTreeItem,
        messagesItem,
        signOutItem,
      ],
    },
  };
};

const getUserMobileMenu = (user) => ({
  items: [thornTreeItem, getUserListsItem(user), signOutItem],
});

const baseDesktopMenu = filterMenu(baseMenu, DESKTOP);
const baseMobileMenu = filterMenu(baseMenu, MOBILE);
baseMobileMenu.items.sort((a, b) => a.mobileOrder - b.mobileOrder);

export default {
  baseMenu,
  baseDesktopMenu,
  baseMobileMenu,
  getSignInItem,
  getUserDesktopMenu,
  getUserMobileMenu,
  getUserProfileItem,
  getUserSettingsItem,
};
